import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getBackendBaseUrl } from '../lib/backendUrl';

const AuthContext = createContext(null);

const backendBaseUrl = getBackendBaseUrl();
const API = `${backendBaseUrl}/api`;
const USER_CACHE_KEY = 'auth_user';
const AUTH_REQUEST_TIMEOUT_MS = 8000;
const SUPPORT_TOKEN_SESSION_KEY = 'trb_support_token_active';

function isAuthUserPayload(value) {
  return Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && (value.id || value._id)
    && typeof value.email === 'string'
  );
}

function requireAuthUserPayload(value) {
  if (isAuthUserPayload(value)) return value;
  const error = new Error('The authentication API returned an invalid response.');
  error.code = 'unexpected_auth_payload';
  throw error;
}

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && !isAuthUserPayload(parsed)) {
      localStorage.removeItem(USER_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function getApiErrorCode(detail) {
  return detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readCachedUser());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cacheUser = useCallback((nextUser) => {
    if (!nextUser) return;
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(nextUser));
    try {
      window.sessionStorage.removeItem('account-ban-status');
    } catch {
      // Storage can be unavailable; successful authentication remains valid.
    }
  }, []);

  const getCachedUser = useCallback(() => {
    return readCachedUser();
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    const { data } = await axios.get(`${API}/auth/me`, {
      withCredentials: true,
      timeout: AUTH_REQUEST_TIMEOUT_MS,
    });
    const nextUser = requireAuthUserPayload(data);
    setUser(nextUser);
    cacheUser(nextUser);
    return nextUser;
  }, [cacheUser]);

  const checkAuth = useCallback(async () => {
    try {
      await fetchCurrentUser();
    } catch (e) {
      const responseStatus = e?.response?.status;

      if (e?.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, {
            withCredentials: true,
            timeout: AUTH_REQUEST_TIMEOUT_MS,
          });
          await fetchCurrentUser();
          return;
        } catch (refreshErr) {
          const refreshStatus = refreshErr?.response?.status;
          if (refreshErr?.response?.status !== 401) {
            console.error('Failed to refresh session:', refreshErr);
          }
          if (refreshStatus === 401 || refreshStatus === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem(USER_CACHE_KEY);
            setUser(false);
            setError(null);
            return;
          }
        }
      }
      if (responseStatus === 403) {
        const detail = e.response?.data?.detail;
        if (getApiErrorCode(detail) === 'account_banned' && typeof window !== 'undefined') {
          try {
            window.sessionStorage.setItem('account-ban-status', JSON.stringify(detail));
          } catch {
            // Ignore storage failures.
          }
          if (!window.location.pathname.includes('account-ban-status') && !window.location.pathname.includes('accountbanstatus')) {
            window.location.replace('/account-ban-status');
          }
        }
        localStorage.removeItem('token');
        localStorage.removeItem(USER_CACHE_KEY);
        setUser(false);
        setError(formatApiErrorDetail(detail));
        return;
      }

      if (responseStatus === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem(USER_CACHE_KEY);
        setUser(false);
        setError(null);
        return;
      }

      const isTransientFailure = !responseStatus || responseStatus >= 500 || e?.code === 'unexpected_auth_payload';
      if (isTransientFailure) {
        const cachedUser = getCachedUser();
        if (cachedUser) {
          setUser(cachedUser);
          setError('We could not refresh your session right now, but you are still signed in locally.');
          return;
        }
      }

      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [fetchCurrentUser, getCachedUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const revokeSupportTokenOnLeave = () => {
      if (window.sessionStorage.getItem(SUPPORT_TOKEN_SESSION_KEY) !== '1') return;
      window.sessionStorage.removeItem(SUPPORT_TOKEN_SESSION_KEY);
      fetch(`${API}/account/support-token/revoke`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }).catch(() => {});
    };

    window.addEventListener('pagehide', revokeSupportTokenOnLeave);
    return () => window.removeEventListener('pagehide', revokeSupportTokenOnLeave);
  }, []);

  const login = async (email, password, mfaCode = '') => {
    setError(null);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, mfa_code: mfaCode || null }, { withCredentials: true });
      const nextUser = requireAuthUserPayload(data);
      setUser(nextUser);
      cacheUser(nextUser);
      return nextUser;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = formatApiErrorDetail(detail);
      setError(msg);
      const error = new Error(msg);
      const code = getApiErrorCode(detail);
      if (code === 'account_banned') {
        error.code = 'account_banned';
        error.banStatus = detail;
      }
      if (code === 'mfa_required' || code === 'mfa_invalid') {
        error.code = code;
      }
      throw error;
    }
  };

  const register = async (payloadOrEmail, password, name) => {
    setError(null);
    try {
      const payload = typeof payloadOrEmail === 'object' && payloadOrEmail !== null
        ? payloadOrEmail
        : { email: payloadOrEmail, password, name };
      const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      const nextUser = requireAuthUserPayload(data);
      if (!nextUser.signup_kyc_required || nextUser.id) {
        setUser(nextUser);
        cacheUser(nextUser);
      }
      return nextUser;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('token');
    localStorage.removeItem(USER_CACHE_KEY);
    sessionStorage.removeItem(SUPPORT_TOKEN_SESSION_KEY);
    setUser(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const { data } = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
    }, { withCredentials: true });
    const nextUser = requireAuthUserPayload(data?.user || data);
    setUser(nextUser);
    cacheUser(nextUser);
    return nextUser;
  };

  const stopImpersonation = async () => {
    const { data } = await axios.post(`${API}/auth/impersonation/stop`, {}, { withCredentials: true });
    const nextUser = requireAuthUserPayload(data?.user || data);
    setUser(nextUser);
    cacheUser(nextUser);
    return nextUser;
  };

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const recordAgreementAcceptance = useCallback((acceptedVersion) => {
    const version = Number(acceptedVersion);
    if (!Number.isInteger(version) || version <= 0) {
      throw new Error('The agreement acceptance response did not include a valid version.');
    }
    const currentUser = user || readCachedUser();
    if (!isAuthUserPayload(currentUser)) {
      throw new Error('Your account session could not be confirmed. Please sign in again.');
    }
    const nextUser = {
      ...currentUser,
      agreement_version_accepted: Math.max(Number(currentUser.agreement_version_accepted) || 0, version),
    };
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(nextUser));
    setUser(nextUser);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, register, logout, changePassword, stopImpersonation, refreshUser, recordAgreementAcceptance }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(fallback = null) {
  const context = useContext(AuthContext) || fallback;
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
